"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { usePathname } from "next/navigation";

// ─── Context ────────────────────────────────────────────────────────────────

interface NavigationProgressContextValue {
  /** Manually start the progress bar (e.g. before a server action redirect). */
  start: () => void;
  /** Manually complete the progress bar. */
  done: () => void;
}

const NavigationProgressContext =
  createContext<NavigationProgressContextValue | null>(null);

export function useNavigationProgress(): NavigationProgressContextValue {
  const ctx = useContext(NavigationProgressContext);
  if (!ctx) {
    throw new Error(
      "useNavigationProgress must be used within NavigationProgressProvider"
    );
  }
  return ctx;
}

// ─── Provider + Bar ─────────────────────────────────────────────────────────

type BarState = "idle" | "loading" | "completing";

export function NavigationProgressProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [barState, setBarState] = useState<BarState>("idle");
  const [progress, setProgress] = useState(0);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pathname = usePathname();
  const prevPathname = useRef(pathname);

  const clearTick = useCallback(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    clearTick();
    setProgress(10);
    setBarState("loading");

    // Gradually advance the bar to give the impression of progress.
    // It asymptotically approaches 90% but never reaches it until `done`.
    tickRef.current = setInterval(() => {
      setProgress((p) => {
        if (p >= 90) return p;
        // Slow down as we get closer to 90
        const increment = (90 - p) * 0.08;
        return Math.min(p + increment, 90);
      });
    }, 200);
  }, [clearTick]);

  const done = useCallback(() => {
    clearTick();
    setProgress(100);
    setBarState("completing");

    // After the fill animation plays, hide the bar
    setTimeout(() => {
      setBarState("idle");
      setProgress(0);
    }, 300);
  }, [clearTick]);

  // Auto-complete when the pathname changes (navigation finished).
  // We track barState via a ref so the effect only depends on pathname.
  const barStateRef = useRef(barState);
  useEffect(() => {
    barStateRef.current = barState;
  }, [barState]);

  useEffect(() => {
    if (pathname !== prevPathname.current) {
      prevPathname.current = pathname;
      if (barStateRef.current === "loading") {
        const raf = requestAnimationFrame(() => done());
        return () => cancelAnimationFrame(raf);
      }
    }
  }, [pathname, done]);

  // Intercept history.pushState to auto-start on client-side navigations
  // triggered by Next.js Link / router.push.
  useEffect(() => {
    const originalPushState = history.pushState.bind(history);
    const originalReplaceState = history.replaceState.bind(history);

    const getPathname = (url: string | URL | null | undefined): string => {
      if (!url) return window.location.pathname;
      try {
        return new URL(String(url), window.location.href).pathname;
      } catch {
        return window.location.pathname;
      }
    };

    history.pushState = function (...args) {
      const url = args[2];
      if (url && getPathname(url as string) !== window.location.pathname) {
        // Defer out of React's useInsertionEffect commit phase — setState is
        // forbidden there (React 19), and Next.js calls pushState during it.
        setTimeout(() => start(), 0);
      }
      return originalPushState(...args);
    };

    history.replaceState = function (...args) {
      const url = args[2];
      if (url && getPathname(url as string) !== window.location.pathname) {
        setTimeout(() => start(), 0);
      }
      return originalReplaceState(...args);
    };

    return () => {
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, [start]);

  // Cleanup on unmount
  useEffect(() => {
    return () => clearTick();
  }, [clearTick]);

  return (
    <NavigationProgressContext.Provider value={{ start, done }}>
      {children}
      {barState !== "idle" && (
        <div
          role="progressbar"
          aria-valuenow={Math.round(progress)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Page loading"
          data-testid="navigation-progress"
          className="pointer-events-none fixed top-0 right-0 left-0 z-[9999] h-[2px]"
        >
          <div
            className="bg-primary h-full transition-all duration-200 ease-out"
            style={{
              width: `${progress}%`,
              opacity: barState === "completing" ? 0 : 1,
              transition:
                barState === "completing"
                  ? "width 150ms ease-out, opacity 250ms ease-out 100ms"
                  : "width 200ms ease-out",
            }}
          />
        </div>
      )}
    </NavigationProgressContext.Provider>
  );
}
